/**
 * Pastille d'état de la file de synchronisation
 * (`ARCHITECTURE_V2.md` §3.5, étape 3).
 *
 * Le §3.5 la décrit en une ligne : « une pastille discrète, **visible seulement
 * quand la file n'est pas vide** (“12 en attente”), et un écran de détail dans
 * les Réglages pour les opérations en échec ».
 *
 * 🔴 **Ni rouge, ni alarmante, et ce n'est pas une question de goût.** Le §3.5
 * proscrit nommément de présenter le hors ligne comme une erreur : « en V2 c'est
 * un mode nominal. Un bandeau rouge “pas de connexion” sur une séance qui se
 * déroule parfaitement serait un mensonge. » La pastille dit ce qu'il y a à
 * dire — combien d'opérations attendent — sans rien dramatiser.
 *
 * ⚠️ **Un échec, lui, se signale.** C'est la seule teinte d'alerte de ce
 * composant, et elle est méritée : une opération `failed` ne partira jamais
 * toute seule (§3.3), donc rien ne la rattrapera si personne ne la voit. Elle
 * mène à l'écran de détail, qui en donne le motif.
 *
 * ⚠️ **Aucun `backdrop-filter` ici, et il n'y en a plus nulle part** depuis la
 * refonte du 25/08/2026 (`styles/surfaces.css`). La pastille est un fond plein,
 * posée *dans* l'en-tête — lui-même plein.
 */

import { Link } from 'react-router';

import { useSyncState } from '../../hooks/useSyncState';
import { TOKENS } from './tokens';

export function SyncBadge() {
  const { pending, failed, paused } = useSyncState();

  // Rien en attente, rien en échec : la pastille n'existe pas. C'est la moitié
  // « discrète » de la demande — un indicateur permanent deviendrait invisible.
  if (pending === 0 && failed.length === 0) return null;

  const enEchec = failed.length > 0;
  const libelle = enEchec
    ? `${String(failed.length)} en échec`
    : `${String(pending)} en attente`;

  return (
    <Link
      to="/reglages/synchronisation"
      aria-label={
        enEchec
          ? `${String(failed.length)} opération(s) en échec — voir le détail`
          : `${String(pending)} saisie(s) en attente d’envoi — voir le détail`
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        // ⚠️ La cible de 44 px est portée par la **hauteur de l'en-tête** et le
        // rembourrage ci-dessous : une pastille de 20 px de haut serait
        // impossible à viser au pouce (specs §8.1).
        minHeight: '44px',
        padding: '0 0.6rem',
        borderRadius: '999px',
        background: enEchec ? TOKENS.errorBg : TOKENS.surfaceRaised,
        border: `1px solid ${enEchec ? 'transparent' : TOKENS.border}`,
        color: enEchec ? TOKENS.errorInk : TOKENS.textQuiet,
        fontSize: '0.8rem',
        fontWeight: 500,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <span aria-hidden>{enEchec ? '!' : paused ? '⏸' : '↻'}</span>
      {libelle}
    </Link>
  );
}
